import { useEffect, useMemo } from "react";
import { useParams, Link, Navigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useScrollReveal } from "../hooks/useScrollReveal";
import { useSeo, SITE_URL } from "../hooks/useSeo";
import { useJsonLd } from "../hooks/useJsonLd";
import { buildServiceLd, buildFaqLd, buildBreadcrumbLd } from "../lib/seo";
import { getTreatmentBySlug, TREATMENTS } from "../config/treatments";
import { SectionEyebrow } from "../components/SectionEyebrow";
import { FaqAccordion } from "../components/FaqAccordion";
import { QuickForm } from "../components/QuickForm";
import { WhatsappLogo } from "@phosphor-icons/react";
import { buildWhatsAppUrl, trackWhatsAppClick } from "../lib/whatsapp";
import { buildLocalizedPath, langFromPath } from "../lib/langRoutes";

const BulletList = ({ items, accent = false }) => (
  <ul className="space-y-3 mt-6">
    {items.map((it) => (
      <li key={it} className="flex items-start gap-3 text-sm font-light text-[#6B7A66] leading-relaxed">
        <span className={`inline-block w-1.5 h-1.5 mt-2 shrink-0 ${accent ? "bg-[#A7CA99]" : "bg-[#3E5239]"}`} />
        <span>{it}</span>
      </li>
    ))}
  </ul>
);

const SectionBlock = ({ title, intro, items, accent }) => {
  const { ref, inView } = useScrollReveal({ threshold: 0.1 });
  return (
    <div ref={ref} className={`reveal ${inView ? "in-view" : ""} bg-[#F6F5F2] border border-[#E4E2DA] p-8 lg:p-10`}>
      <h3 className="font-serif text-2xl lg:text-3xl font-light text-[#3E5239]">{title}</h3>
      {intro && <p className="mt-3 text-sm font-light text-[#6B7A66]">{intro}</p>}
      <BulletList items={items} accent={accent} />
    </div>
  );
};

const TreatmentPage = () => {
  const params = useParams();
  const { pathname } = useLocation();
  const lang = langFromPath(pathname);
  // Either `/tratamentos/:slug` or direct `/botox` etc. Strip language prefix and leading "/" for direct routes.
  const rest = pathname.replace(/^\/(pt|en)\//, "/").replace(/^\//, "");
  const slug = params.slug || rest.split("/")[0];
  const { t } = useTranslation();
  const tr = getTreatmentBySlug(slug);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  const relatedTreatments = useMemo(
    () => (tr ? TREATMENTS.filter((x) => x.key !== tr.key) : []),
    [tr]
  );

  // SEO meta — title + description always include "Zürich"
  const seo = t(`treatment_seo.${tr?.key}`, { returnObjects: true, defaultValue: {} });
  useSeo({
    title: seo?.title,
    description: seo?.description,
    canonicalPath: tr ? `/${tr.slug}` : undefined,
    image: tr?.hero?.startsWith("http") ? tr.hero : tr?.hero ? `${SITE_URL}${tr.hero}` : undefined,
  });

  // Structured data: Service + FAQPage + Breadcrumb
  const faqItems = t(`treatment_faq.${tr?.key}`, { returnObjects: true, defaultValue: [] });
  const item = tr ? t(`treatments.items.${tr.key}`, { returnObjects: true }) : null;
  const ldData = useMemo(() => {
    if (!tr || !item) return [];
    return [
      buildServiceLd({
        name: item.title,
        description: seo?.description || item.short_desc,
        slug: tr.slug,
        fromPrice: item.price,
      }),
      buildFaqLd(faqItems),
      buildBreadcrumbLd([
        { name: "Home", path: "/" },
        { name: t("nav.treatments"), path: "/tratamentos" },
        { name: item.title, path: `/${tr.slug}` },
      ]),
    ].filter(Boolean);
  }, [tr?.slug]);
  useJsonLd(ldData, tr?.slug || "unknown");

  if (!tr) return <Navigate to={buildLocalizedPath("treatments", lang)} replace />;

  return (
    <div data-testid={`treatment-page-${tr.key}`} className="bg-[#F6F5F2]">
      {/* Hero */}
      <section className="pt-36 lg:pt-44 pb-16 lg:pb-24 bg-[#F6F5F2] relative overflow-hidden">
        <div className="absolute top-32 -right-20 w-96 h-96 rounded-full bg-[#A7CA99]/15 blur-3xl pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12">
          <Link to={buildLocalizedPath("treatments", lang)} data-testid="back-to-treatments" className="overline text-[#3E5239] hover:text-[#A7CA99] transition-colors inline-block mb-8">
            {t("treatment_page.back")}
          </Link>

          <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-end">
            <div className="lg:col-span-7">
              <SectionEyebrow num="01">{item.subtitle}</SectionEyebrow>
              <h1 data-testid="treatment-page-title" className="font-serif text-5xl lg:text-7xl font-light tracking-tight leading-[0.95] text-[#3E5239]">
                {item.title}
              </h1>
              <p className="mt-8 max-w-xl text-base lg:text-lg font-light text-[#6B7A66] leading-relaxed">
                {item.hero_paragraph}
              </p>
              <div className="mt-10 flex items-center gap-6">
                <div className="overline text-[#A7CA99] text-base">{item.price}</div>
                <a
                  href={buildWhatsAppUrl(`Tenho interesse em: ${item.title}`)}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => trackWhatsAppClick(`treatment_page_${tr.slug}`)}
                  data-testid="treatment-page-cta"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-[#25D366] hover:bg-[#1DA851] text-white text-xs uppercase tracking-[0.2em] transition-colors"
                >
                  <WhatsappLogo size={16} weight="fill" />
                  {t("nav.book")}
                </a>
              </div>
            </div>
            <div className="lg:col-span-5">
              <div className="relative aspect-[4/5] overflow-hidden bg-[#EFEDE6]">
                <img src={tr.hero} alt={`${item.title} · KosMedico Zürich`} className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-[#A7CA99]/8 mix-blend-multiply" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About */}
      <section className="py-20 lg:py-28 bg-[#EFEDE6] border-y border-[#E4E2DA]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <SectionEyebrow num="02">{t("treatment_page.about_title")}</SectionEyebrow>
            <h2 className="font-serif text-3xl lg:text-5xl font-light text-[#3E5239] leading-tight">
              {item.title}
            </h2>
          </div>
          <div className="lg:col-span-7 space-y-6 text-[#6B7A66] font-light leading-relaxed">
            {item.long_paragraphs.map((p, i) => (
              <p key={p.slice(0, 32)} data-testid={`treatment-paragraph-${i}`}>{p}</p>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery */}
      <section className="py-16 bg-[#F6F5F2]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-[#E4E2DA]">
            {tr.gallery.map((src) => (
              <div key={src} className="relative aspect-[4/3] overflow-hidden bg-[#EFEDE6] group">
                <img src={src} alt={`${item.title} · KosMedico Zürich`} loading="lazy" className="w-full h-full object-cover transition-transform duration-[1200ms] group-hover:scale-[1.05]" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits + Contraindications + Before + After */}
      <section className="py-20 lg:py-28 bg-[#F6F5F2]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 space-y-px bg-[#E4E2DA] grid md:grid-cols-2 gap-px">
          <SectionBlock title={t("treatment_page.benefits_title")} items={item.benefits} accent />
          <SectionBlock title={t("treatment_page.contra_title")} intro={t("treatment_page.contra_intro")} items={item.contraindications} />
          <SectionBlock title={t("treatment_page.before_title")} items={item.before} />
          <SectionBlock title={t("treatment_page.after_title")} items={item.after} accent />
        </div>
        <p className="max-w-7xl mx-auto px-6 md:px-12 text-xs text-[#6B7A66]/70 italic mt-8">
          {t("treatment_page.disclaimer")}
        </p>
      </section>

      {/* Other treatments */}
      <section className="py-20 bg-[#EFEDE6] border-y border-[#E4E2DA]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <SectionEyebrow num="03">{t("nav.treatments")}</SectionEyebrow>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-[#E4E2DA]">
            {relatedTreatments.map((other) => (
              <Link
                key={other.key}
                to={buildLocalizedPath("treatmentDirect", lang, other.slug)}
                data-testid={`related-treatment-${other.key}`}
                className="group relative bg-[#F6F5F2] hover:bg-[#A7CA99]/10 transition-colors aspect-[4/5] overflow-hidden block"
              >
                <img src={other.hero} alt={`${t(`treatments.items.${other.key}.title`)} · ${t("faq.in_zurich", "in Zürich")} · KosMedico`} loading="lazy" className="w-full h-full object-cover opacity-90 group-hover:scale-[1.04] transition-transform duration-[1200ms]" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#3E5239]/80 via-[#3E5239]/20 to-transparent" />
                <div className="absolute bottom-5 left-4 right-4 lg:bottom-6 lg:left-6 lg:right-6">
                  <div className="overline text-[#A7CA99] mb-1 text-[10px] leading-tight break-words">{t(`treatments.items.${other.key}.subtitle`)}</div>
                  <div className="font-serif text-lg sm:text-xl lg:text-2xl text-[#F6F5F2] font-light leading-tight break-words">{t(`treatments.items.${other.key}.title`)}</div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Per-treatment FAQ */}
      {Array.isArray(faqItems) && faqItems.length > 0 && (
        <section
          id={`faq-${tr.slug}`}
          data-testid="treatment-page-faq"
          className="py-24 lg:py-32 bg-[#F6F5F2]"
        >
          <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-12 lg:gap-16">
            <div className="lg:col-span-4">
              <SectionEyebrow num="04">{t("faq.eyebrow")}</SectionEyebrow>
              <h2 className="font-serif text-3xl lg:text-4xl xl:text-5xl font-light tracking-tight leading-[1.05] text-[#3E5239]">
                {item.title}
                <br />
                <span className="italic text-[#A7CA99]">{t("faq.in_zurich", "in Zürich")}.</span>
              </h2>
              <a
                href={buildWhatsAppUrl(`Tenho interesse em: ${item.title}`)}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackWhatsAppClick(`treatment_faq_${tr.slug}`)}
                data-testid="treatment-faq-whatsapp"
                className="mt-8 inline-flex items-center gap-2 px-5 py-3 bg-[#25D366] hover:bg-[#1DA851] text-white text-[11px] uppercase tracking-[0.2em] transition-colors"
              >
                <WhatsappLogo size={14} weight="fill" />
                {t("faq.cta_whatsapp", "Conversar no WhatsApp")}
              </a>
            </div>
            <div className="lg:col-span-8">
              <FaqAccordion items={faqItems} />
            </div>
          </div>
        </section>
      )}

      {/* Lead Form on every product page */}
      <QuickForm />
    </div>
  );
};

export default TreatmentPage;
